import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useMarkInvitedMutation, useMeQuery } from '../profiles/api';
import { useLastingLinkQuery } from '../inviteLinks/api';
import { copy, ShareKitSheet } from '../onboarding/ShareKit';
import { inviteShareText, withVia } from '../onboarding/share';
import { track, type DeliveredPromptSurface } from '../onboarding/track';
import {
  deliveredRound,
  restDeliveredPrompt,
  showDeliveredPrompt,
  type DeliveredPromptMemory,
  type DeliveredRound,
  type PanelConversation,
} from './homeData';

/**
 * "Who else deserves a letter from you?" — asked once, calmly, after one of
 * my letters lands. No counts, no streaks: one card, "Not now" for a week,
 * and the share it offers carries `via=delivered_prompt` so a referred signup
 * can say where it came from.
 */

const KEY = 'toiyp:delivered-prompt';
const EMPTY: DeliveredPromptMemory = { letterId: null, restUntil: null };

/** The round it first appeared for in this visit — it stays up until the next one. */
let shownThisVisit: string | null = null;
/** Rounds whose `delivered_prompt_shown` has gone, so re-renders don't count twice. */
const counted = new Set<string>();

function readMemory(): DeliveredPromptMemory {
  try {
    const v = localStorage.getItem(KEY);
    return v ? { ...EMPTY, ...(JSON.parse(v) as Partial<DeliveredPromptMemory>) } : EMPTY;
  } catch {
    return EMPTY;
  }
}

function writeMemory(m: DeliveredPromptMemory): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(m));
  } catch {
    /* private mode: it may ask again next visit, once */
  }
}

/** The round to ask about now, or null; and "Not now". */
export function useDeliveredPrompt(conversations: PanelConversation[]) {
  const [memory, setMemory] = useState(readMemory);
  const round = useMemo(() => deliveredRound(conversations, new Date()), [conversations]);
  const show = showDeliveredPrompt(round, memory, new Date(), shownThisVisit);

  // Remember the round as soon as it shows, so a later visit doesn't ask again.
  useEffect(() => {
    if (!show || !round || memory.letterId === round.letterId) return;
    shownThisVisit = round.letterId;
    const next = { ...memory, letterId: round.letterId };
    writeMemory(next);
    setMemory(next);
  }, [show, round, memory]);

  const dismiss = useCallback(() => {
    if (!round) return;
    const next = restDeliveredPrompt(round.letterId, new Date());
    shownThisVisit = null;
    writeMemory(next);
    setMemory(next);
  }, [round]);

  return { round: show ? round : null, dismiss };
}

export function DeliveredPromptCard({
  round,
  surface,
  onDismiss,
}: {
  round: DeliveredRound;
  surface: DeliveredPromptSurface;
  onDismiss: () => void;
}) {
  const { data: me } = useMeQuery();
  const { data: link } = useLastingLinkQuery();
  const [markInvited] = useMarkInvitedMutation();
  const [kit, setKit] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (counted.has(round.letterId)) return;
    counted.add(round.letterId);
    track({ event: 'delivered_prompt_shown', surface });
  }, [round.letterId, surface]);

  const onShare = useCallback(async () => {
    if (!link || !me?.handle) return;
    track({ event: 'delivered_prompt_clicked', surface, action: 'share' });
    setMsg(null);
    const text = inviteShareText(me.handle, withVia(link.url, 'delivered_prompt'));
    if (typeof navigator.share === 'function') {
      track({ event: 'share_opened', channel: 'sheet', from: 'delivered_prompt' });
      try {
        await navigator.share({ title: 'toiyp', text });
        void markInvited();
        return;
      } catch (e) {
        // Closing the share sheet is a choice, not an error.
        if ((e as { name?: string }).name === 'AbortError') return;
      }
    }
    track({ event: 'share_opened', channel: 'copy', from: 'delivered_prompt' });
    if (await copy(text)) {
      void markInvited();
      setMsg('Copied. Paste it wherever you talk to them.');
    } else {
      setMsg(text);
    }
  }, [link, me?.handle, surface, markInvited]);

  return (
    <section className="nudge" aria-labelledby="delivered-prompt-title">
      <button
        type="button"
        className="nudge__close"
        aria-label="Not now"
        onClick={() => {
          track({ event: 'delivered_prompt_dismissed', surface });
          onDismiss();
        }}
      >
        <X size={16} aria-hidden="true" />
      </button>
      <p className="nudge__context">Your letter reached {round.peerName}</p>
      <h2 className="nudge__title" id="delivered-prompt-title">
        Who else deserves a letter from you?
      </h2>
      <p className="nudge__note">
        Send your toiyp to someone you’d love to hear from properly. When they join and write their
        first letter, you both get a welcome stamp.
      </p>
      <div className="nudge__actions">
        <button
          type="button"
          className="home-btn home-btn--ink nudge__cta"
          disabled={!link || !me?.handle}
          onClick={() => void onShare()}
        >
          Send my toiyp
        </button>
        <button
          type="button"
          className="home-link nudge__all"
          onClick={() => {
            track({ event: 'delivered_prompt_clicked', surface, action: 'more_ways' });
            setKit(true);
          }}
        >
          More ways
        </button>
      </div>
      {msg && (
        <p className="nudge__note" role="status">
          {msg}
        </p>
      )}
      {/* Portalled: the letters list is a containing block that would clip a fixed sheet. */}
      {kit && createPortal(<ShareKitSheet from="delivered_prompt" onClose={() => setKit(false)} />, document.body)}
    </section>
  );
}

/** Wide screens and tall phones: the card on its own, where the short-phone nudge sits. */
export function DeliveredPrompt({ conversations }: { conversations: PanelConversation[] }) {
  const { round, dismiss } = useDeliveredPrompt(conversations);
  if (!round) return null;
  return <DeliveredPromptCard round={round} surface="list" onDismiss={dismiss} />;
}
